import { useState } from 'react';
import styled from 'styled-components';

import { Tooltip } from '../../../cdl/Tooltip/Tooltip';
import { translate } from '../../helpers/translate.helper';
import { getSidebarShortcutLabel, useSidebar } from '../../hooks/useSidebar';
import { IconChevronCompactLeft } from '../../icons/cdl/ChevronCompactLeft';
import { IconChevronCompactRight } from '../../icons/cdl/ChevronCompactRight';
import { theme } from '../../ui/theme';

interface ToggleButtonProps {
    $isFixed: boolean;
    $isHovered: boolean;
}

const ToggleButtonContainer = styled.div<ToggleButtonProps>`
    position: absolute;
    top: 0;
    left: 220px;
    width: 30px;
    height: 100vh;
    pointer-events: auto;
`;

const ToggleButton = styled.button`
    width: 30px;
    height: 100vh;
    background: transparent;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    position: relative;
`;

const LineIndicator = styled.div<{ $isHovered: boolean }>`
    width: 2px;
    height: 18px;
    background-color: ${theme.colors.border.muted};
    border-radius: 1px;
    transition: opacity 0.2s ease-out;
    opacity: ${({ $isHovered }) => ($isHovered ? 0 : 1)};
    position: absolute;
`;

const ChevronIcon = styled.div<{ $isHovered: boolean }>`
    transition: opacity 0.2s ease-out;
    opacity: ${({ $isHovered }) => ($isHovered ? 1 : 0)};
    position: absolute;
    color: ${theme.colors.border.default};

    svg {
        color: inherit;
    }
`;

const HoverArea = styled.div<{ $top: number; $height: number }>`
    position: absolute;
    top: ${({ $top }) => $top}px;
    left: 220px;
    width: 15px;
    height: ${({ $height }) => $height}px;
    background: transparent;
    pointer-events: auto;
`;

export const SidebarEdgeToggle = () => {
    const { isFixed, isHovered, toggle, setHovered } = useSidebar();
    const [isButtonHovered, setIsButtonHovered] = useState(false);
    const shortcut = getSidebarShortcutLabel();

    const tooltipText = isFixed ? translate('navigation.hideSidebar') : translate('navigation.showSidebar');

    const Icon = isFixed ? IconChevronCompactLeft : IconChevronCompactRight;

    const handleClick = () => {
        toggle();
        setHovered(false);
    };

    const handleMouseEnter = () => {
        setIsButtonHovered(true);
        if (!isFixed && isHovered) {
            setHovered(true);
        }
    };

    const handleMouseLeave = () => {
        setIsButtonHovered(false);
        if (!isFixed && isHovered) {
            setHovered(false);
        }
    };

    const handleHoverAreaEnter = () => {
        if (!isFixed) {
            setHovered(true);
        }
    };

    const handleHoverAreaLeave = () => {
        if (!isFixed) {
            setHovered(false);
        }
    };

    const totalHeight = typeof window !== 'undefined' ? window.innerHeight : 1000;
    const gapSize = 96;
    const topAreaHeight = (totalHeight - gapSize) / 2;
    const bottomAreaTop = topAreaHeight + gapSize;
    const bottomAreaHeight = totalHeight - bottomAreaTop;

    return (
        <>
            <ToggleButtonContainer $isFixed={isFixed} $isHovered={isHovered}>
                <Tooltip label={`${tooltipText} ${shortcut}`} side="right" align="center">
                    <ToggleButton onClick={handleClick} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                        <LineIndicator $isHovered={isButtonHovered} />
                        <ChevronIcon $isHovered={isButtonHovered}>
                            <Icon width={24} height={24} />
                        </ChevronIcon>
                    </ToggleButton>
                </Tooltip>
            </ToggleButtonContainer>

            {!isFixed && !isHovered && (
                <>
                    <HoverArea
                        $top={0}
                        $height={topAreaHeight}
                        onMouseEnter={handleHoverAreaEnter}
                        onMouseLeave={handleHoverAreaLeave}
                    />
                    <HoverArea
                        $top={bottomAreaTop}
                        $height={bottomAreaHeight}
                        onMouseEnter={handleHoverAreaEnter}
                        onMouseLeave={handleHoverAreaLeave}
                    />
                </>
            )}
        </>
    );
};
